/**
 * GeoMappingTab — Column mapping visualization for geospatial CSVs.
 *
 * Shows source CSV columns on the left connected by bezier edges to
 * the SpectreGeoTracking parquet target schema on the right, grouped into:
 *   - Core Coordinates (lat/lon/timestamp/altitude)
 *   - Entity Identity (entity_id, entity_name, entity_type per entity)
 *   - Metadata → JSON blob
 *   - System (auto-filled, non-connectable)
 *
 * Entity property columns are handled automatically and hidden from this view.
 */

import {
  Background,
  type Connection,
  Controls,
  type Edge,
  Handle,
  MarkerType,
  type Node,
  type NodeProps,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from '@xyflow/react'
import { useCallback, useEffect, useMemo } from 'react'
import '@xyflow/react/dist/style.css'
import type { WizardConfiguration } from '@/services/api/cases'
import { cn } from '@/utils/cn'
import { getColumnSamples } from './helpers'
import type { TabularDataState } from './types'

// =============================================================================
// Types
// =============================================================================

interface GeoMappingTabProps {
  wizardConfig: WizardConfiguration
  availableColumns: string[]
  tabularData: TabularDataState | null
  onConfigChange: (updated: WizardConfiguration) => void
}

interface GeoMapping {
  latitudeColumn: string
  longitudeColumn: string
  timestampColumn: string
  altitudeColumn: string | null
  timestampFormat: string | null
  entityMappings: Array<{
    entityType: string
    color: string
    identifierColumn: string
    nameColumn: string
    propertyColumns: Record<string, string> // stored with entity, hidden from UI
  }>
  metadataColumns: Record<string, string> // → metadata JSON blob
}

// Category colors
const CORE_COLOR = '#3b82f6' // blue — core coordinates
const PROPERTY_COLOR = '#8b5cf6' // purple — entity properties → Neo4j
const METADATA_COLOR = '#6b7280' // gray — metadata JSON
const SYSTEM_COLOR = '#404040' // dimmed — auto-filled system columns

// System columns that are auto-filled (not user-mappable)
const SYSTEM_COLUMNS = [
  'snapped_latitude',
  'snapped_longitude',
  'source_file',
  'case_id',
  'document_id',
] as const

// =============================================================================
// Custom Nodes
// =============================================================================

interface SourceColumnData extends Record<string, unknown> {
  columnName: string
  samples: string[]
  isMapped: boolean
  mappedColor: string | null
}

function SourceColumnNode({ data }: NodeProps<Node<SourceColumnData>>) {
  return (
    <div
      className={cn(
        'px-3 py-2 rounded-md border text-xs min-w-[180px]',
        'bg-neutral-900',
        data.isMapped ? 'border-neutral-600 text-white' : 'border-neutral-800 text-neutral-500'
      )}
    >
      <div className="flex items-center gap-2">
        <div
          className="w-2 h-2 rounded-full shrink-0"
          style={{
            backgroundColor: data.isMapped
              ? ((data.mappedColor as string) ?? '#9ca3af')
              : '#404040',
          }}
        />
        <span className="font-medium truncate">{data.columnName as string}</span>
      </div>
      {(data.samples as string[]).length > 0 && (
        <div className="mt-1 text-[10px] text-neutral-500 truncate pl-4">
          e.g. {(data.samples as string[]).slice(0, 2).join(', ')}
        </div>
      )}
      <Handle type="source" position={Position.Right} className="!bg-neutral-600 !w-2 !h-2" />
    </div>
  )
}

interface TargetFieldData extends Record<string, unknown> {
  fieldName: string
  categoryColor: string
  isConnected: boolean
  isStatic: boolean
  staticValue: string | null
  suffix: string | null
}

function TargetFieldNode({ data }: NodeProps<Node<TargetFieldData>>) {
  const isStatic = data.isStatic as boolean

  return (
    <div
      className={cn(
        'px-3 py-2 rounded-md border text-xs min-w-[180px]',
        'bg-neutral-900',
        isStatic
          ? 'border-neutral-800 text-neutral-400'
          : data.isConnected
            ? 'border-neutral-600 text-white'
            : 'border-neutral-800 text-neutral-500'
      )}
    >
      {!isStatic && (
        <Handle type="target" position={Position.Left} className="!bg-neutral-600 !w-2 !h-2" />
      )}
      <div className="flex items-center gap-2">
        <div
          className={cn('w-2 h-2 rounded-full shrink-0', isStatic && 'border border-neutral-600')}
          style={{ backgroundColor: isStatic ? 'transparent' : (data.categoryColor as string) }}
        />
        <span className="font-medium">{data.fieldName as string}</span>
        {isStatic && data.staticValue && (
          <span
            className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-medium"
            style={{
              backgroundColor: `${data.categoryColor as string}20`,
              color: data.categoryColor as string,
            }}
          >
            {data.staticValue as string}
          </span>
        )}
        {!isStatic && data.suffix && (
          <span className="ml-auto text-[10px] text-neutral-600">{data.suffix as string}</span>
        )}
      </div>
    </div>
  )
}

interface SystemFieldData extends Record<string, unknown> {
  fieldName: string
}

function SystemFieldNode({ data }: NodeProps<Node<SystemFieldData>>) {
  return (
    <div className="px-3 py-2 rounded-md border border-neutral-800/50 text-xs min-w-[180px] bg-neutral-950">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full shrink-0 border border-neutral-700" />
        <span className="text-neutral-600">{data.fieldName as string}</span>
      </div>
    </div>
  )
}

interface CategoryHeaderData extends Record<string, unknown> {
  title: string
  subtitle: string | null
  categoryColor: string
}

function CategoryHeaderNode({ data }: NodeProps<Node<CategoryHeaderData>>) {
  return (
    <div className="px-3 py-1.5">
      <div
        className="text-xs font-semibold tracking-wide uppercase"
        style={{ color: data.categoryColor as string }}
      >
        {data.title as string}
      </div>
      {data.subtitle && (
        <div className="text-[10px] text-neutral-600 mt-0.5">{data.subtitle as string}</div>
      )}
    </div>
  )
}

interface MetadataSinkData extends Record<string, unknown> {
  mappedColumns: Array<{ key: string; sourceCol: string }>
  categoryColor: string
}

function MetadataSinkNode({ data }: NodeProps<Node<MetadataSinkData>>) {
  const mapped = data.mappedColumns as Array<{ key: string; sourceCol: string }>
  return (
    <div
      className={cn(
        'px-3 py-2 rounded-md border text-xs min-w-[180px]',
        'bg-neutral-900',
        mapped.length > 0 ? 'border-neutral-600' : 'border-neutral-800 border-dashed'
      )}
    >
      <Handle type="target" position={Position.Left} className="!bg-neutral-600 !w-2 !h-2" />
      <div className="flex items-center gap-2">
        <div
          className="w-2 h-2 rounded-full shrink-0"
          style={{ backgroundColor: data.categoryColor as string }}
        />
        <span className="font-medium text-neutral-400">metadata</span>
        <span className="ml-auto text-[10px] text-neutral-600">&rarr; JSON</span>
      </div>
      {mapped.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1 pl-4">
          {mapped.map(m => (
            <span
              key={m.key}
              className="px-1.5 py-0.5 rounded text-[10px] bg-neutral-800 text-neutral-400"
            >
              {m.key}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

const nodeTypes = {
  sourceColumn: SourceColumnNode,
  targetField: TargetFieldNode,
  systemField: SystemFieldNode,
  categoryHeader: CategoryHeaderNode,
  metadataSink: MetadataSinkNode,
}

// =============================================================================
// Helpers — parse WizardConfiguration → GeoMapping
// =============================================================================

function parseGeoMapping(config: WizardConfiguration): GeoMapping {
  const gm = config.geo_mapping

  // If geo_mapping exists, read directly from it
  if (gm) {
    const entityMappings = (gm.entity_mappings ?? []).map(em => {
      const origEntity = config.entities.find(e => e.entity_type === em.entity_type)
      return {
        entityType: em.entity_type,
        color: origEntity?.color ?? '#3b82f6',
        identifierColumn: em.entity_identifier_column,
        nameColumn: em.entity_name_column,
        propertyColumns: { ...em.entity_property_columns },
      }
    })

    return {
      latitudeColumn: gm.latitude_column ?? '',
      longitudeColumn: gm.longitude_column ?? '',
      timestampColumn: gm.timestamp_column ?? '',
      altitudeColumn: gm.altitude_column ?? null,
      timestampFormat: gm.timestamp_format ?? null,
      entityMappings,
      metadataColumns: { ...(gm.metadata_columns ?? {}) },
    }
  }

  // Fallback: existing GeoObservation entity logic (for user-edited configs)
  const geoObs = config.entities.find(e => e.entity_type === 'GeoObservation')
  const pm = geoObs?.properties_mapping ?? {}

  // Core observation fields
  const latitudeColumn = pm.latitude ?? ''
  const longitudeColumn = pm.longitude ?? ''
  const timestampColumn = pm.timestamp ?? ''
  const altitudeColumn = pm.altitude ?? null
  const timestampFormat = pm.timestamp_format ?? null

  // Everything else in GeoObservation's properties_mapping = metadata
  const reservedKeys = new Set([
    'latitude',
    'longitude',
    'timestamp',
    'altitude',
    'timestamp_format',
  ])
  const metadataColumns: Record<string, string> = {}
  for (const [key, val] of Object.entries(pm)) {
    if (!reservedKeys.has(key)) {
      metadataColumns[key] = val
    }
  }

  // Tracked entities (non-GeoObservation)
  const entityMappings = config.entities
    .filter(e => e.entity_type !== 'GeoObservation')
    .map(e => {
      const propsCopy = { ...e.properties_mapping }
      const nameColumn = propsCopy.name ?? ''
      delete propsCopy.name
      return {
        entityType: e.entity_type,
        color: e.color,
        identifierColumn: e.identifier_column ?? '',
        nameColumn,
        propertyColumns: propsCopy,
      }
    })

  return {
    latitudeColumn,
    longitudeColumn,
    timestampColumn,
    altitudeColumn,
    timestampFormat,
    entityMappings,
    metadataColumns,
  }
}

// Rebuild WizardConfiguration from GeoMapping
function rebuildConfig(original: WizardConfiguration, mapping: GeoMapping): WizardConfiguration {
  const allMappedCols = new Set<string>()

  // GeoObservation entity
  const obsProps: Record<string, string> = {}
  if (mapping.latitudeColumn) {
    obsProps.latitude = mapping.latitudeColumn
    allMappedCols.add(mapping.latitudeColumn)
  }
  if (mapping.longitudeColumn) {
    obsProps.longitude = mapping.longitudeColumn
    allMappedCols.add(mapping.longitudeColumn)
  }
  if (mapping.timestampColumn) {
    obsProps.timestamp = mapping.timestampColumn
    allMappedCols.add(mapping.timestampColumn)
  }
  if (mapping.altitudeColumn) {
    obsProps.altitude = mapping.altitudeColumn
    allMappedCols.add(mapping.altitudeColumn)
  }
  if (mapping.timestampFormat) {
    obsProps.timestamp_format = mapping.timestampFormat
  }
  for (const [key, val] of Object.entries(mapping.metadataColumns)) {
    obsProps[key] = val
    allMappedCols.add(val)
  }

  const geoObsEntity = original.entities.find(e => e.entity_type === 'GeoObservation')
  const obsEntity = {
    ...(geoObsEntity ?? {
      entity_type: 'GeoObservation',
      label: 'GeoObservation',
      static_properties: {},
      color: CORE_COLOR,
      row_condition: null,
    }),
    properties_mapping: obsProps,
    columns: Object.values(obsProps).filter(v => v && !v.includes('%')), // exclude format strings
  }

  // Tracked entities
  const trackedEntities = mapping.entityMappings.map(em => {
    const origEnt = original.entities.find(
      e => e.entity_type === em.entityType && e.entity_type !== 'GeoObservation'
    )
    const pm: Record<string, string> = {}
    if (em.nameColumn) {
      pm.name = em.nameColumn
      allMappedCols.add(em.nameColumn)
    }
    for (const [key, val] of Object.entries(em.propertyColumns)) {
      pm[key] = val
      allMappedCols.add(val)
    }
    if (em.identifierColumn) allMappedCols.add(em.identifierColumn)

    return {
      ...(origEnt ?? {
        entity_type: em.entityType,
        label: em.entityType,
        static_properties: {},
        row_condition: null,
      }),
      color: em.color,
      identifier_column: em.identifierColumn || null,
      properties_mapping: pm,
      columns: [...(em.identifierColumn ? [em.identifierColumn] : []), ...Object.values(pm)],
    }
  })

  // Compute unmapped
  const allColumns = original.entities.flatMap(e => e.columns)
  const allSrcCols = new Set([...allColumns, ...allMappedCols])
  const unmapped = Array.from(allSrcCols).filter(c => !allMappedCols.has(c))

  const result: WizardConfiguration = {
    ...original,
    entities: [obsEntity as any, ...trackedEntities],
    unmapped_columns: unmapped,
  }

  // Keep geo_mapping in sync with user edits
  if (original.geo_mapping) {
    result.geo_mapping = {
      ...original.geo_mapping,
      latitude_column: mapping.latitudeColumn,
      longitude_column: mapping.longitudeColumn,
      timestamp_column: mapping.timestampColumn,
      altitude_column: mapping.altitudeColumn,
      timestamp_format: mapping.timestampFormat,
      metadata_columns: { ...mapping.metadataColumns },
      entity_mappings: mapping.entityMappings.map(em => ({
        entity_type: em.entityType,
        entity_identifier_column: em.identifierColumn,
        entity_name_column: em.nameColumn,
        entity_property_columns: { ...em.propertyColumns },
      })),
    }
  }

  return result
}

// =============================================================================
// Build ReactFlow graph from GeoMapping
// =============================================================================

interface MappingEdge {
  sourceColumnName: string
  targetNodeId: string
  color: string
}

function buildGraph(
  mapping: GeoMapping,
  availableColumns: string[],
  tabularData: TabularDataState | null
) {
  const nodes: Node[] = []
  const edges: Edge[] = []
  const mappingEdges: MappingEdge[] = []

  // Collect all mappings for coloring source nodes
  const columnToColor = new Map<string, string>()

  // --- Core coordinate mappings ---
  const coreFields: { key: string; label: string; value: string }[] = [
    { key: 'latitude', label: 'latitude', value: mapping.latitudeColumn },
    { key: 'longitude', label: 'longitude', value: mapping.longitudeColumn },
    { key: 'timestamp', label: 'timestamp', value: mapping.timestampColumn },
  ]
  if (mapping.altitudeColumn) {
    coreFields.push({ key: 'altitude', label: 'altitude', value: mapping.altitudeColumn })
  }

  for (const f of coreFields) {
    if (f.value) {
      columnToColor.set(f.value, CORE_COLOR)
      mappingEdges.push({
        sourceColumnName: f.value,
        targetNodeId: `core-${f.key}`,
        color: CORE_COLOR,
      })
    }
  }

  // --- Entity identity mappings ---
  for (const em of mapping.entityMappings) {
    if (em.identifierColumn) {
      columnToColor.set(em.identifierColumn, em.color)
      mappingEdges.push({
        sourceColumnName: em.identifierColumn,
        targetNodeId: `ent-${em.entityType}-entity_id`,
        color: em.color,
      })
    }
    if (em.nameColumn) {
      columnToColor.set(em.nameColumn, em.color)
      mappingEdges.push({
        sourceColumnName: em.nameColumn,
        targetNodeId: `ent-${em.entityType}-entity_name`,
        color: em.color,
      })
    }
  }

  // --- Entity property columns — tracked internally but not shown in the graph.
  // Mark them as mapped so they don't appear as unmapped columns.
  for (const em of mapping.entityMappings) {
    for (const [, propCol] of Object.entries(em.propertyColumns)) {
      columnToColor.set(propCol, PROPERTY_COLOR)
    }
  }

  // --- Metadata mappings (all target the single meta-sink node) ---
  for (const [, col] of Object.entries(mapping.metadataColumns)) {
    columnToColor.set(col, METADATA_COLOR)
    mappingEdges.push({ sourceColumnName: col, targetNodeId: 'meta-sink', color: METADATA_COLOR })
  }

  // Connected target node IDs
  const connectedTargets = new Set(mappingEdges.map(e => e.targetNodeId))

  // ---- LEFT: Source column nodes ----
  const ROW_HEIGHT = 52
  const LEFT_X = 50
  const RIGHT_X = 500

  availableColumns.forEach((col, i) => {
    const samples = tabularData
      ? getColumnSamples(col, tabularData.columns, tabularData.rows, 2)
      : []
    nodes.push({
      id: `src-${col}`,
      type: 'sourceColumn',
      position: { x: LEFT_X, y: i * ROW_HEIGHT },
      data: {
        columnName: col,
        samples,
        isMapped: columnToColor.has(col),
        mappedColor: columnToColor.get(col) ?? null,
      },
    })
  })

  // ---- RIGHT: Target field nodes grouped by actual parquet schema ----
  let rightY = 0

  // --- Group 1: Core Coordinates ---
  nodes.push({
    id: 'header-core',
    type: 'categoryHeader',
    position: { x: RIGHT_X, y: rightY },
    data: {
      title: 'Core Coordinates',
      subtitle: '\u2192 parquet columns',
      categoryColor: CORE_COLOR,
    },
    selectable: false,
    draggable: false,
  })
  rightY += 42

  for (const f of coreFields) {
    const nodeId = `core-${f.key}`
    nodes.push({
      id: nodeId,
      type: 'targetField',
      position: { x: RIGHT_X, y: rightY },
      data: {
        fieldName: f.label,
        categoryColor: CORE_COLOR,
        isConnected: connectedTargets.has(nodeId),
        isStatic: false,
        staticValue: null,
        suffix: null,
      },
    })
    rightY += ROW_HEIGHT
  }

  // --- Group 2: Entity Identity (per entity type) ---
  for (const em of mapping.entityMappings) {
    rightY += 20
    nodes.push({
      id: `header-ent-${em.entityType}`,
      type: 'categoryHeader',
      position: { x: RIGHT_X, y: rightY },
      data: {
        title: `Entity: ${em.entityType}`,
        subtitle: '\u2192 entity_id, entity_name, entity_type',
        categoryColor: em.color,
      },
      selectable: false,
      draggable: false,
    })
    rightY += 42

    // entity_id
    const idNodeId = `ent-${em.entityType}-entity_id`
    nodes.push({
      id: idNodeId,
      type: 'targetField',
      position: { x: RIGHT_X, y: rightY },
      data: {
        fieldName: 'entity_id',
        categoryColor: em.color,
        isConnected: connectedTargets.has(idNodeId),
        isStatic: false,
        staticValue: null,
        suffix: null,
      },
    })
    rightY += ROW_HEIGHT

    // entity_name
    const nameNodeId = `ent-${em.entityType}-entity_name`
    nodes.push({
      id: nameNodeId,
      type: 'targetField',
      position: { x: RIGHT_X, y: rightY },
      data: {
        fieldName: 'entity_name',
        categoryColor: em.color,
        isConnected: connectedTargets.has(nameNodeId),
        isStatic: false,
        staticValue: null,
        suffix: null,
      },
    })
    rightY += ROW_HEIGHT

    // entity_type — static, shows the entity type as a badge
    nodes.push({
      id: `ent-${em.entityType}-entity_type`,
      type: 'targetField',
      position: { x: RIGHT_X, y: rightY },
      data: {
        fieldName: 'entity_type',
        categoryColor: em.color,
        isConnected: false,
        isStatic: true,
        staticValue: em.entityType,
        suffix: null,
      },
    })
    rightY += ROW_HEIGHT
  }

  // --- Entity Properties are handled automatically (hidden from UI) ---

  // --- Group 3: Metadata → JSON blob (always visible, single sink node) ---
  rightY += 20
  nodes.push({
    id: 'header-meta',
    type: 'categoryHeader',
    position: { x: RIGHT_X, y: rightY },
    data: {
      title: 'Metadata',
      subtitle: '\u2192 parquet metadata JSON',
      categoryColor: METADATA_COLOR,
    },
    selectable: false,
    draggable: false,
  })
  rightY += 42

  const metaEntries = Object.entries(mapping.metadataColumns)
  nodes.push({
    id: 'meta-sink',
    type: 'metadataSink',
    position: { x: RIGHT_X, y: rightY },
    data: {
      mappedColumns: metaEntries.map(([key, col]) => ({ key, sourceCol: col })),
      categoryColor: METADATA_COLOR,
    },
  })
  rightY += ROW_HEIGHT + (metaEntries.length > 0 ? 16 : 0)

  // --- Group 4: System (auto-filled, non-connectable) ---
  rightY += 20
  nodes.push({
    id: 'header-system',
    type: 'categoryHeader',
    position: { x: RIGHT_X, y: rightY },
    data: { title: 'System', subtitle: 'auto-filled', categoryColor: SYSTEM_COLOR },
    selectable: false,
    draggable: false,
  })
  rightY += 42

  for (const col of SYSTEM_COLUMNS) {
    nodes.push({
      id: `sys-${col}`,
      type: 'systemField',
      position: { x: RIGHT_X, y: rightY },
      data: { fieldName: col },
      selectable: false,
      connectable: false,
    })
    rightY += ROW_HEIGHT
  }

  // ---- EDGES ----
  for (const me of mappingEdges) {
    edges.push({
      id: `edge-${me.sourceColumnName}-${me.targetNodeId}`,
      source: `src-${me.sourceColumnName}`,
      target: me.targetNodeId,
      type: 'smoothstep',
      animated: true,
      style: { stroke: me.color, strokeWidth: 2 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: me.color,
      },
    })
  }

  return { nodes, edges }
}

// =============================================================================
// Main Component
// =============================================================================

export function GeoMappingTab({
  wizardConfig,
  availableColumns,
  tabularData,
  onConfigChange,
}: GeoMappingTabProps) {
  const mapping = useMemo(() => parseGeoMapping(wizardConfig), [wizardConfig])

  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => buildGraph(mapping, availableColumns, tabularData),
    [mapping, availableColumns, tabularData]
  )

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)

  useEffect(() => {
    setNodes(initialNodes)
  }, [initialNodes, setNodes])
  useEffect(() => {
    setEdges(initialEdges)
  }, [initialEdges, setEdges])

  // Handle new connections (drag from source to target)
  const onConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return
      const sourceCol = connection.source.replace(/^src-/, '')
      const targetId = connection.target

      // Parse target to figure out which mapping to update
      const newMapping = { ...mapping }
      // Deep-clone entity mappings
      newMapping.entityMappings = mapping.entityMappings.map(em => ({
        ...em,
        propertyColumns: { ...em.propertyColumns },
      }))
      newMapping.metadataColumns = { ...mapping.metadataColumns }

      if (targetId.startsWith('core-')) {
        const field = targetId.replace('core-', '')
        if (field === 'latitude') newMapping.latitudeColumn = sourceCol
        else if (field === 'longitude') newMapping.longitudeColumn = sourceCol
        else if (field === 'timestamp') newMapping.timestampColumn = sourceCol
        else if (field === 'altitude') newMapping.altitudeColumn = sourceCol
      } else if (targetId.startsWith('ent-')) {
        const parts = targetId.replace('ent-', '').split('-')
        const entityType = parts[0]
        const field = parts.slice(1).join('-')
        const entMapping = newMapping.entityMappings.find(em => em.entityType === entityType)
        if (entMapping) {
          if (field === 'entity_id') entMapping.identifierColumn = sourceCol
          else if (field === 'entity_name') entMapping.nameColumn = sourceCol
        }
      } else if (targetId === 'meta-sink') {
        newMapping.metadataColumns[sourceCol] = sourceCol // key = column name
      }

      onConfigChange(rebuildConfig(wizardConfig, newMapping))
    },
    [mapping, wizardConfig, onConfigChange]
  )

  // Handle edge deletion (click edge to remove mapping)
  const onEdgeClick = useCallback(
    (_: React.MouseEvent, edge: Edge) => {
      const targetId = edge.target

      const newMapping = { ...mapping }
      newMapping.entityMappings = mapping.entityMappings.map(em => ({
        ...em,
        propertyColumns: { ...em.propertyColumns },
      }))
      newMapping.metadataColumns = { ...mapping.metadataColumns }

      if (targetId.startsWith('core-')) {
        const field = targetId.replace('core-', '')
        if (field === 'latitude') newMapping.latitudeColumn = ''
        else if (field === 'longitude') newMapping.longitudeColumn = ''
        else if (field === 'timestamp') newMapping.timestampColumn = ''
        else if (field === 'altitude') newMapping.altitudeColumn = null
      } else if (targetId.startsWith('ent-')) {
        const parts = targetId.replace('ent-', '').split('-')
        const entityType = parts[0]
        const field = parts.slice(1).join('-')
        const entMapping = newMapping.entityMappings.find(em => em.entityType === entityType)
        if (entMapping) {
          if (field === 'entity_id') entMapping.identifierColumn = ''
          else if (field === 'entity_name') entMapping.nameColumn = ''
        }
      } else if (targetId === 'meta-sink') {
        const sourceCol = edge.source.replace(/^src-/, '')
        const keyToRemove = Object.entries(newMapping.metadataColumns).find(
          ([, col]) => col === sourceCol
        )?.[0]
        if (keyToRemove) {
          delete newMapping.metadataColumns[keyToRemove]
        }
      }

      onConfigChange(rebuildConfig(wizardConfig, newMapping))
    },
    [mapping, wizardConfig, onConfigChange]
  )

  if (availableColumns.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-neutral-500">
        <div className="text-center">
          <p className="text-sm">No columns available</p>
          <p className="text-xs mt-1">Upload a geospatial CSV to see column mappings</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full w-full flex flex-col">
      {/* Info banner */}
      <div className="flex items-center gap-2 px-3 py-2 bg-blue-950/30 border-b border-blue-900/30 text-[11px] text-blue-400/80 shrink-0">
        <span>&#8505;</span>
        <span>
          Mapping columns to <span className="font-semibold text-blue-300">SpectreGeoTracking</span>{' '}
          parquet format. Drag connections from source columns to target fields. Click an edge to
          remove it.
        </span>
      </div>
      {/* ReactFlow canvas */}
      <div className="flex-1 min-h-0">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onEdgeClick={onEdgeClick}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          proOptions={{ hideAttribution: true }}
          className="bg-neutral-950"
          minZoom={0.3}
          maxZoom={1.5}
          nodesDraggable={true}
          nodesConnectable={true}
          elementsSelectable={true}
        >
          <Background color="#262626" gap={20} size={1} />
          <Controls className="!bg-neutral-800 !border-neutral-700 !rounded-lg [&>button]:!bg-neutral-800 [&>button]:!border-neutral-700 [&>button]:!text-neutral-400 [&>button:hover]:!bg-neutral-700" />
        </ReactFlow>
      </div>
    </div>
  )
}

export default GeoMappingTab
